import { LangCode } from 'api-client'
import { SingleIncludedUserResourcePermissionResource } from 'api-user-client'
import { useMutation, useQueryClient } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'

import { setUserLanguage as setUserLanguageApi } from '~shared-components/api/users'
import { ENABLED_LANGUAGES } from '~shared-components/components/FlagRender/frozenFlags'
import { setLocale } from '~shared-components/redux/actions/config.actions'
import { redirectToThunk } from '~shared-components/redux/actions/redirect.actions'
import { initTranslations } from '~shared-components/redux/actions/translations.actions'
import { LocationSelector } from '~shared-components/redux/selectors/location.selector'
import { TranslationsSelector } from '~shared-components/redux/selectors/translations.selector'
import { getTranslations } from '~shared-components/translations/translations'
import {
  QueryCacheKey,
  SupportedCountry,
  SupportedLanguage,
} from '~shared-components/utils/enums'
import { getLocaleFromCountryAndLanguage } from '~shared-components/utils/locale'
import {
  validateCountry,
  validateLanguage,
  validateLanguageAgainstFlag,
} from '~shared-components/utils/locale/locale-utils'

import { useToaster } from './useToaster'
import { useTranslations } from './useTranslations'

export const useSelectUserLanguage = () => {
  const dispatch = useDispatch()
  const currentCountry = useSelector(LocationSelector.country)
  const pageType = useSelector(LocationSelector.pageType)
  const currentLanguage = useSelector(TranslationsSelector.currentLanguage)

  const { showErrorToast } = useToaster()
  const queryClient = useQueryClient()
  const t = useTranslations()

  const dispatchSelectedLanguage = (
    language: SupportedLanguage | LangCode,
    country?: SupportedCountry,
  ) => {
    const locale = getLocaleFromCountryAndLanguage(
      validateCountry(country ?? currentCountry),
      validateLanguage(language),
    )
    dispatch(setLocale(locale))

    const selectedLanguage = validateLanguageAgainstFlag(
      language,
      ENABLED_LANGUAGES,
    )

    if (selectedLanguage !== currentLanguage && language) {
      dispatch(
        initTranslations(getTranslations(selectedLanguage), selectedLanguage),
      )
    }

    dispatch(
      redirectToThunk({
        payload: {
          country: locale.country,
          language: locale.language,
        },
        type: pageType,
      }),
    )
  }

  const { isLoading, mutateAsync: setUserLanguage } = useMutation<
    SingleIncludedUserResourcePermissionResource,
    unknown,
    { country?: SupportedCountry; language: LangCode }
  >(({ language }) => setUserLanguageApi(language), {
    onError: () => {
      showErrorToast(t('toast.error.languagechange'))
    },
    onSuccess: (_user, { country, language }) => {
      queryClient.invalidateQueries([QueryCacheKey.User, 'me'])

      dispatchSelectedLanguage(language, country)
    },
  })

  return {
    currentLanguage,
    dispatchSelectedLanguage,
    isLoading,
    setUserLanguage,
  }
}
